import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, startWith } from 'rxjs';
import { LocalizationService } from '../services/localization.service';

/**
 * A service responsible for managing and providing the PrimeNG library's locale based on the selected language.
 *
 * This service relies on the TranslateService from ngx-translate/core to dynamically update the locale
 * based on language changes. It provides an Observable stream of locale changes that components can subscribe
 * to in order to react to language changes.
 */
@Injectable({
    providedIn: 'root',
})
export class PrimengLocaleService {
    private readonly locale$: Observable<string> | undefined = new Observable();

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly localizationService: LocalizationService,
        private readonly translateService: TranslateService
    ) {
        // Get stream of locales based on currently selected language
        this.locale$ = this.translateService.onLangChange.pipe(
            startWith({
                lang: this.localizationService.defaultLanguage,
                translations: {},
            }),
            map((result) => result.lang),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    /**
     * @returns returns an Observable that emits the current locale whenever it changes. Components can subscribe
     * to this Observable to react to language changes and update their content accordingly.
     */
    getLocale(): Observable<string> | undefined {
        return this.locale$;
    }
}
